import {
  Tag,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { PatientDetail } from '@/lib/patientDetail';
import { TagIconRow } from '@/components/tags/TagIconBadge';
import { ContactInfoHeader } from '@/components/patients/ContactInfoHeader';
import type { PatientSummary } from '@/types/patient.types';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/hooks/useAuth';
import {
  SectionHeader,
  PatientQuickStatsSection,
  EditableNoteSection,
  PlansSection,
  MembershipsSection,
  UpcomingAppointmentsSection,
  RecentVisitsSection,
  RecentMessagesSection,
} from '@/components/patients/PatientDashboardSections';

interface DetailProfileProps {
  detail: PatientDetail;
  loading: boolean;
  onSectionNavigate?: (section: string) => void;
  /**
   * Optional row-level data (from a list row click). When provided, the contact
   * header and Total Spend stat render from this immediately, while the full
   * detail query continues fetching in the background.
   */
  seedPatient?: PatientSummary | null;
}

export function DetailProfile({ detail, loading, onSectionNavigate, seedPatient }: DetailProfileProps) {
  const { accountId: authAccountId } = useAuth();
  const accountQuery = useQuery({
    queryKey: ['account-details'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: Record<string, unknown> }>('/account-details');
      return r.data?.data ?? {};
    },
    staleTime: 10 * 60 * 1000,
  });
  const domainEmailReply = String(
    (accountQuery.data as Record<string, unknown> | undefined)?.domainEmailReply ?? '',
  ).trim();
  const patient = detail.patient as Record<string, string> | null;
  const phoneNumber =
    patient?.cellPhone ??
    patient?.phone ??
    patient?.homePhone ??
    seedPatient?.cellPhone ??
    seedPatient?.phone ??
    seedPatient?.homePhone ??
    null;
  // The dial code of whichever phone was picked above: the cell's for the cell,
  // the home phone's for `phone`/`homePhone`. A list-row seed carries none (+1).
  const phoneCountryCode =
    patient?.cellPhone != null
      ? patient.cellCountryCode
      : patient?.phone != null || patient?.homePhone != null
        ? patient.phoneCountryCode
        : null;
  const patientId = patient?.patientID
    ? Number(patient.patientID)
    : seedPatient?.patientId ?? null;
  const patientNameFromDetail = patient
    ? `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim()
    : '';
  const patientName =
    patientNameFromDetail || (seedPatient?.displayName ?? '').trim();
  // Tri-state (see usePatientPhoto): once the detail is loaded it is
  // authoritative — a URL, or null for "known none". Before that, the list-row
  // seed decides; undefined there means "unknown" and keeps the by-id lookup.
  const detailPhotoUrl = patient?.photoUrl as string | null | undefined;
  const photoUrl: string | null | undefined = patient
    ? (typeof detailPhotoUrl === 'string' || detailPhotoUrl === null ? detailPhotoUrl : undefined)
    : seedPatient?.photoUrl;
  const address = patient?.address
    ? [patient.address, patient.city, patient.state].filter(Boolean).join(', ') + (patient.zip ? ' ' + patient.zip : '')
    : null;
  const email = patient?.email ?? seedPatient?.email ?? null;
  const birthDate = patient?.birthDate ?? seedPatient?.dateOfBirth ?? null;
  const facilityName = patient?.facilityName ?? seedPatient?.facilityName ?? null;
  const effectiveAccountId =
    (patient as Record<string, unknown> | null)?.accountId ?? authAccountId ?? 0;
  const commLogEmail =
    domainEmailReply && patientId
      ? `acct${effectiveAccountId}-0-0-${patientId}@${domainEmailReply}`
      : null;
  const seedTotalSpend =
    typeof seedPatient?.totalSpent === 'number' ? seedPatient.totalSpent : null;
  const showCoreSkeleton = loading && !patient && !seedPatient;

  const separator = <div className="h-px bg-slate-200 my-1" />;

  if (showCoreSkeleton) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-white">
        <div className="flex-1 min-h-0 w-full overflow-y-auto">
          <div className="p-5 space-y-5">
            <div className="flex gap-3">
              <div className="shrink-0 w-[42%] flex items-start justify-center">
                <div className="h-20 w-20 rounded-full bg-slate-200 animate-pulse" />
              </div>
              <div className="flex-1 min-w-0 space-y-2 pt-1">
                <div className="h-4 w-3/4 rounded bg-slate-200 animate-pulse" />
                <div className="h-3 w-1/2 rounded bg-slate-200 animate-pulse" />
                <div className="h-3 w-2/3 rounded bg-slate-200 animate-pulse" />
                <div className="h-3 w-1/2 rounded bg-slate-200 animate-pulse" />
              </div>
            </div>
            {separator}
            <div className="grid grid-cols-2 gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="h-2.5 w-16 rounded bg-slate-200 animate-pulse" />
                  <div className="mt-2 h-4 w-12 rounded bg-slate-200 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-white">
      <div className="flex-1 min-h-0 w-full overflow-y-auto">
        <div className="p-5 space-y-5">

          <ContactInfoHeader
            id={patientId ?? 0}
            name={patientName || 'Patient'}
            photoUrl={photoUrl}
            phone={phoneNumber}
            phoneCountryCode={phoneCountryCode}
            email={email}
            address={address}
            birthDate={birthDate}
            facilityName={facilityName}
            commLogEmail={commLogEmail}
          />

          {separator}

          <PatientQuickStatsSection patientId={patientId} seedTotalSpend={seedTotalSpend} />

          {separator}

          <PlansSection patientId={patientId} onSectionNavigate={onSectionNavigate} />

          {separator}

          {/* ── Tags ── */}
          <div>
            <SectionHeader icon={Tag} label="Tags" />
            <TagIconRow
              tags={detail.tags}
              emptyFallback={<span className="text-xs text-slate-500">No tags</span>}
            />
          </div>

          {separator}

          {/* ── Notes ── */}
          <EditableNoteSection note={detail.note} patientId={patientId} />

          {separator}

          <MembershipsSection patientId={patientId} onSectionNavigate={onSectionNavigate} />

          {separator}

          <UpcomingAppointmentsSection patientId={patientId} onSectionNavigate={onSectionNavigate} />

          {separator}

          <RecentVisitsSection patientId={patientId} onSectionNavigate={onSectionNavigate} />

          {separator}

          <RecentMessagesSection patientId={patientId} onSectionNavigate={onSectionNavigate} />
        </div>
      </div>
    </div>
  );
}
